/**
 * kpiRowUtils.js
 * Pure utility functions — no React, no hooks, fully testable.
 * Extracted from the original KpiRow.jsx monolith.
 */

/* ── Trigger helpers ── */

const rangesOverlap = (aFrom, aTo, bFrom, bTo) => aFrom <= bTo && bFrom <= aTo;

const daysBetween = (from, to) => {
  if (!from || !to) return 0;
  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (isNaN(ms)) return 0;
  return Math.floor(ms / 86400000) + 1;
};

export const aggregateTriggerRange = (triggers) => {
  const mins = triggers.map((t) => t.minTargetData).filter((v) => v != null && v !== "" && !isNaN(Number(v))).map(Number);
  const maxes = triggers.map((t) => t.maxTargetData).filter((v) => v != null && v !== "" && !isNaN(Number(v))).map(Number);
  return {
    min: mins.length ? String(Math.min(...mins)) : "—",
    max: maxes.length ? String(Math.max(...maxes)) : "—",
    unit: triggers[0]?.unit || "",
  };
};

export const aggregateTriggerNumeric = (triggers) => {
  const mins = triggers.map((t) => t.minTargetData).filter((v) => v != null && v !== "" && !isNaN(Number(v))).map(Number);
  const maxes = triggers.map((t) => t.maxTargetData).filter((v) => v != null && v !== "" && !isNaN(Number(v))).map(Number);
  return {
    min: mins.length ? Math.min(...mins) : null,
    max: maxes.length ? Math.max(...maxes) : null,
    unit: triggers[0]?.unit || "",
  };
};

export const resolveTriggers = (allTriggers, questionId, fromDate, toDate) => {
  if (!allTriggers?.length) return { triggers: [], isFallback: false };
  const forKpi = allTriggers.filter((t) => t.questionId === questionId);
  if (!forKpi.length) return { triggers: [], isFallback: false };

  if (!fromDate || !toDate) {
    const latest = [...forKpi].sort((a, b) => (b.fromDate || "").localeCompare(a.fromDate || ""))[0];
    return { triggers: [latest], isFallback: true };
  }
  const exact = forKpi.filter((t) => t.fromDate === fromDate && t.toDate === toDate);
  if (exact.length) return { triggers: exact, isFallback: false };
  const overlapping = forKpi.filter((t) => rangesOverlap(t.fromDate, t.toDate, fromDate, toDate));
  if (!overlapping.length) return { triggers: [], isFallback: false };
  const widest = overlapping.reduce((best, t) =>
    daysBetween(t.fromDate, t.toDate) > daysBetween(best.fromDate, best.toDate) ? t : best
  );
  return { triggers: [widest], isFallback: true };
};

export const computeProjection = (value, selFrom, selTo, triggers) => {
  if (!triggers?.length || !selFrom || !selTo) return null;
  const trigger = triggers[0];
  const daysSel = daysBetween(selFrom, selTo);
  const daysTrig = daysBetween(trigger.fromDate, trigger.toDate);
  if (!daysSel || !daysTrig) return null;
  const val = Number(value) || 0;
  const projected = (val / daysSel) * daysTrig;
  const { min, max, unit } = aggregateTriggerNumeric(triggers);
  let status = null;
  if (max != null && projected > max) status = "above";
  else if (min != null && projected < min) status = "below";
  else if (min != null || max != null) status = "within";
  return { projected, min, max, unit, daysSel, daysTrig, status };
};

/* ── Status computation ── */

export const computeKpiStatus = (item, currentUserId, isAdmin) => {
  const rawHistory = item.quantative_history || [];
  const history = isAdmin
    ? rawHistory
    : rawHistory.filter((ans) => Number(ans.createdById) === Number(currentUserId));
  const total = history.length;

  let text = "Pending";
  let color = "#d97706";
  let bg = "#fef3c7";

  if (total > 0) {
    const accepted = history.filter((a) => a.status?.toLowerCase() === "approved").length;
    const rejected = history.filter((a) => a.status?.toLowerCase() === "rejected").length;
    const sub = (a) => (a.submissionStatus || a.submission_status || "").toLowerCase();
    const submitted = history.filter((a) => sub(a) === "submitted_for_review").length;
    const returned = history.filter((a) => sub(a) === "returned_to_contributor").length;
    const drafts = history.filter((a) => sub(a) === "draft").length;
    const inAudit = history.filter((a) => sub(a) === "sent_to_audit").length;

    if (accepted === total) { text = "Accepted"; color = "#16a34a"; bg = "#dcfce7"; }
    else if (rejected > 0) { text = "Rejected"; color = "#dc2626"; bg = "#fee2e2"; }
    else if (submitted > 0) { text = "Pending Review"; color = "#d97706"; bg = "#fef3c7"; }
    else if (returned > 0) { text = "Returned"; color = "#dc2626"; bg = "#fee2e2"; }
    else if (inAudit > 0) { text = "In Audit"; color = "#2563eb"; bg = "#dbeafe"; }
    else if (drafts > 0) { text = "Draft"; color = "#64748b"; bg = "#f1f5f9"; }
    else { text = `${accepted} / ${total} Accepted`; }
  }

  return { text, color, bg };
};

/* ── Auditor normalization ── */

export const normalizeAuditors = (auditors) => {
  const map = new Map();
  (Array.isArray(auditors) ? auditors : []).forEach((a) => {
    const uid = a?.userId ?? a?.id;
    if (uid == null) return;
    const key = String(uid);
    const level = a.auditLevel ?? a.level;
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        name: a.name || a.email || `User ${uid}`,
        first_name: a.firstName || a.first_name || "",
        last_name: a.lastName || a.last_name || "",
        email: a.email || "",
        levels: new Set(),
      });
    }
    if (level != null) map.get(key).levels.add(level);
  });
  return [...map.values()]
    .map((u) => {
      const lv = [...u.levels].sort((x, y) => x - y);
      return { ...u, meta: lv.length ? `Level ${lv.join(", ")}` : "" };
    })
    .sort((x, y) => x.name.localeCompare(y.name));
};

/* ── Analytics parsing ── */

export const computeAnalytics = (analytics) => {
  const safeAnalytics = analytics || {};
  return {
    currentFY: Number(safeAnalytics.fy_value || 0),
    previousFY: Number(safeAnalytics.previous_fy_value || 0),
    currentSelected: Number(safeAnalytics.selected_date_range_value || 0),
    previousSelected: Number(safeAnalytics.previous_selected_date_range_value || 0),
    currentFYDraft: Number(safeAnalytics.fy_value_draft || 0),
    previousFYDraft: Number(safeAnalytics.previous_fy_value_draft || 0),
    currentSelectedDraft: Number(safeAnalytics.selected_date_range_value_draft || 0),
    previousSelectedDraft: Number(safeAnalytics.previous_selected_date_range_value_draft || 0),
    emissionCurrent: Number(safeAnalytics.fy_emission || 0),
    emissionPrevious: Number(safeAnalytics.previous_fy_emission || 0),
    emissionUnit: safeAnalytics.emission_unit || "kgCO2e",
    energyCurrent: Number(safeAnalytics.fy_energy || 0),
    energyPrevious: Number(safeAnalytics.previous_fy_energy || 0),
    energyUnit: safeAnalytics.energy_unit || "GJ",
    noxCurrent: Number(safeAnalytics.fy_nox || 0),
    noxPrevious: Number(safeAnalytics.previous_fy_nox || 0),
    soxCurrent: Number(safeAnalytics.fy_sox || 0),
    soxPrevious: Number(safeAnalytics.previous_fy_sox || 0),
    ppmCurrent: Number(safeAnalytics.fy_ppm || 0),
    ppmPrevious: Number(safeAnalytics.previous_fy_ppm || 0),
  };
};

/* ── Projection status styles ── */

export const STATUS_STYLES = {
  above: { bg: "#fee2e2", color: "#dc2626", icon: "⚠", label: "Will cross max" },
  below: { bg: "#fef3c7", color: "#d97706", icon: "↓", label: "Below min" },
  within: { bg: "#dcfce7", color: "#16a34a", icon: "✓", label: "On track" },
};

/**
 * Resolve the shared trigger display fields consumed by both the inline
 * TriggerBadge and the expanded TriggerSection — single source of truth for
 * the aggregated range and the projection status style.
 *
 * @param {Array}  triggers   — resolved triggers for this KPI + date range
 * @param {object} projection — computeProjection result (may be null)
 * @returns {{ min: string, max: string, unit: string, statusStyle: object|null }}
 */
export const resolveTriggerDisplay = (triggers, projection) => {
  const { min, max, unit } = aggregateTriggerRange(triggers);
  const statusStyle = projection?.status ? STATUS_STYLES[projection.status] : null;
  return { min, max, unit, statusStyle };
};

export const fmtNum = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
